import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot,
  getCountFromServer
} from 'firebase/firestore';
import { db, storage } from '../firebase/config.js';
import { deleteObject, ref as storageRef } from 'firebase/storage';

// ===== SERVICIO DE EVENTOS =====
export const eventsService = {
  // Obtener todos los eventos del organizador
  async getOrganizerEvents(organizerId) {
    try {
      const q = query(
        collection(db, 'events'),
        where('organizerId', '==', organizerId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        eventId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting organizer events:', error);
      throw error;
    }
  },

  // Obtener evento por ID
  async getEventById(eventId) {
    try {
      const docRef = doc(db, 'events', eventId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          eventId: docSnap.id,
          ...docSnap.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting event:', error);
      throw error;
    }
  },

  // Crear nuevo evento
  async createEvent(eventData) {
    try {
      const eventWithTimestamp = {
        ...eventData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        totalPhotos: 0,
        totalParticipants: 0,
        status: 'draft'
      };
      
      const docRef = await addDoc(collection(db, 'events'), eventWithTimestamp);
      return {
        eventId: docRef.id,
        ...eventWithTimestamp
      };
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  // Actualizar evento
  async updateEvent(eventId, updateData) {
    try {
      const docRef = doc(db, 'events', eventId);
      const extra = {};
      // Sincronizar permisos públicos con estado cuando aplique
      // - active: habilita lecturas públicas (Screen/Shot)
      // - paused: NO cambia permisos; solo afecta la vista de Screen
      // - completed: deshabilita lecturas públicas para todas las apps
      if (typeof updateData?.status === 'string') {
        if (updateData.status === 'active') {
          extra.allowPublicPhotos = true;
        } else if (updateData.status === 'inactive') {
          extra.allowPublicPhotos = false;
        } else {
          // Para cualquier otro estado legacy, considerar inactivo (sin migración aún)
          extra.allowPublicPhotos = false;
        }
      }
      await updateDoc(docRef, {
        ...updateData,
        ...extra,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },

  // Eliminar evento
  async deleteEvent(eventId) {
    try {
      await deleteDoc(doc(db, 'events', eventId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },

  // Generar código de unión único
  async generateUniqueJoinCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let joinCode;
    let isUnique = false;
    
    while (!isUnique) {
      joinCode = '';
      for (let i = 0; i < 6; i++) {
        joinCode += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      
      // Verificar si el código ya existe
      const q = query(collection(db, 'events'), where('joinCode', '==', joinCode));
      const querySnapshot = await getDocs(q);
      isUnique = querySnapshot.empty;
    }
    
    return joinCode;
  }
};

// ===== SERVICIO DE PANTALLAS =====
export const screensService = {
  // Obtener todas las pantallas del organizador
  async getOrganizerScreens() {
    try {
      const q = query(
        collection(db, 'screens'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        screenId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting screens:', error);
      throw error;
    }
  },

  // Crear nueva pantalla
  async createScreen(screenData) {
    try {
      const screenWithTimestamp = {
        ...screenData,
        status: 'available',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isOnline: false
      };
      
      const docRef = await addDoc(collection(db, 'screens'), screenWithTimestamp);
      return {
        screenId: docRef.id,
        ...screenWithTimestamp
      };
    } catch (error) {
      console.error('Error creating screen:', error);
      throw error;
    }
  },

  // Vincular pantalla a evento
  async linkScreenToEvent(screenId, eventId, linkedBy) {
    try {
      const docRef = doc(db, 'screens', screenId);
      await updateDoc(docRef, {
        linkedEventId: eventId,
        linkedAt: serverTimestamp(),
        linkedBy,
        status: 'linked',
        updatedAt: serverTimestamp()
      });

      // Crear log de la acción
      await addDoc(collection(db, 'screenLogs'), {
        screenId,
        eventId,
        action: 'linked',
        details: { linkedBy },
        timestamp: serverTimestamp(),
        userId: linkedBy
      });

      return { success: true };
    } catch (error) {
      console.error('Error linking screen to event:', error);
      throw error;
    }
  },

  // Desvincular pantalla de evento
  async unlinkScreenFromEvent(screenId, unlinkedBy) {
    try {
      const screenDoc = await getDoc(doc(db, 'screens', screenId));
      const eventId = screenDoc.data()?.linkedEventId;

      const docRef = doc(db, 'screens', screenId);
      await updateDoc(docRef, {
        linkedEventId: null,
        linkedAt: null,
        linkedBy: null,
        status: 'available',
        updatedAt: serverTimestamp()
      });

      // Crear log de la acción
      if (eventId) {
        await addDoc(collection(db, 'screenLogs'), {
          screenId,
          eventId,
          action: 'unlinked',
          details: { unlinkedBy },
          timestamp: serverTimestamp(),
          userId: unlinkedBy
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error unlinking screen from event:', error);
      throw error;
    }
  }
};

// ===== SERVICIO DE FOTOS =====
export const photosService = {
  // Obtener fotos de un evento
  async getEventPhotos(eventId, status = 'approved') {
    try {
      const q = query(
        collection(db, 'photos'),
        where('eventId', '==', eventId),
        where('status', '==', status),
        orderBy('uploadedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        photoId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting event photos:', error);
      throw error;
    }
  },

  // Obtener fotos pendientes de moderación
  async getPendingPhotos(eventId) {
    try {
      const q = query(
        collection(db, 'photos'),
        where('eventId', '==', eventId),
        where('status', '==', 'pending'),
        orderBy('uploadedAt', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        photoId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pending photos:', error);
      throw error;
    }
  },

  // Moderar foto
  async moderatePhoto(photoId, status, moderatedBy, moderationNote = '') {
    try {
      const docRef = doc(db, 'photos', photoId);
      // Leer estado previo antes de actualizar para ajustar contadores correctamente
      const beforeSnap = await getDoc(docRef);
      const prevStatus = beforeSnap.exists() ? (beforeSnap.data()?.status || 'pending') : 'pending';
      await updateDoc(docRef, {
        status,
        moderatedBy,
        moderatedAt: serverTimestamp(),
        moderationNote
      });

      // Actualizar contador de fotos en el evento
      const afterSnap = await getDoc(docRef);
      const eventId = afterSnap.data()?.eventId;
      
      if (eventId) {
        const eventRef = doc(db, 'events', eventId);
        const eventDoc = await getDoc(eventRef);
        const currentTotal = eventDoc.data()?.totalPhotos || 0;
        const wasApproved = prevStatus === 'approved';
        const isApproved = status === 'approved';
        if (!wasApproved && isApproved) {
          await updateDoc(eventRef, { totalPhotos: currentTotal + 1 });
        } else if (wasApproved && !isApproved) {
          await updateDoc(eventRef, { totalPhotos: Math.max(0, currentTotal - 1) });
        } // else, no cambio efectivo en aprobadas
      }

      return { success: true };
    } catch (error) {
      console.error('Error moderating photo:', error);
      throw error;
    }
  }
  ,
  // Eliminar definitivamente una foto (Firestore + Storage si es posible)
  async deletePhoto(photoId, imageURL) {
    try {
      // 1) Borrar doc en Firestore
      await deleteDoc(doc(db, 'photos', photoId));

      // 2) Intentar borrar el archivo en Storage si podemos inferir la ruta
      const path = (() => {
        if (!imageURL) return null;
        try {
          // URL típica: https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encodedPath>?alt=media&token=...
          const m = imageURL.match(/\/o\/([^?]+)/);
          if (m && m[1]) return decodeURIComponent(m[1]);
        } catch (_) {}
        return null;
      })();

      if (path) {
        try {
          const ref = storageRef(storage, path);
          await deleteObject(ref);
        } catch (e) {
          // No interrumpir si falla el borrado de storage (p.ej. permisos o ya no existe)
          console.warn('Storage delete error for', path, e);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  }
};

// ===== SERVICIO DE PARTICIPANTES =====
export const participantsService = {
  // Obtener participantes de un evento
  async getEventParticipants(eventId) {
    try {
      const q = query(
        collection(db, 'eventParticipants'),
        where('eventId', '==', eventId),
        orderBy('joinedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        participantId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting event participants:', error);
      throw error;
    }
  },

  // Escuchar participantes en tiempo real
  onEventParticipantsSnapshot(eventId, cb) {
    try {
      const baseRef = collection(db, 'eventParticipants');
      const q1 = query(baseRef, where('eventId', '==', eventId), orderBy('joinedAt', 'desc'));
      // Intentar con índice; si falla por índice, usar fallback sin orderBy
      const unsub = onSnapshot(q1, (snap) => {
        const list = snap.docs.map(d => ({ participantId: d.id, ...d.data() }));
        cb(list);
      }, (err) => {
        console.warn('Participants listener (with orderBy) failed, falling back:', err?.message || err);
        const q2 = query(baseRef, where('eventId', '==', eventId));
        onSnapshot(q2, (snap2) => {
          const list = snap2.docs.map(d => ({ participantId: d.id, ...d.data() }));
          // ordenar en cliente por joinedAt desc si existe
          list.sort((a,b) => {
            const ta = a.joinedAt?.toDate ? a.joinedAt.toDate().getTime() : new Date(a.joinedAt || 0).getTime();
            const tb = b.joinedAt?.toDate ? b.joinedAt.toDate().getTime() : new Date(b.joinedAt || 0).getTime();
            return tb - ta;
          });
          cb(list);
        });
      });
      return unsub;
    } catch (error) {
      console.error('Error listening participants:', error);
      throw error;
    }
  },

  // Agregar participante a evento
  async addParticipantToEvent(eventId, userId, participantData = {}) {
    try {
      const participantId = `${userId}_${eventId}`;
      const participantWithTimestamp = {
        eventId,
        userId,
        userEmail: participantData.userEmail || null,
        status: 'active',
        joinedAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        canUploadPhotos: true,
        canViewPhotos: true,
        canInviteOthers: false,
        photosUploaded: 0,
        ...participantData
      };

      await setDoc(doc(db, 'eventParticipants', participantId), participantWithTimestamp);

      // Actualizar contador de participantes en el evento
      const eventRef = doc(db, 'events', eventId);
      const eventDoc = await getDoc(eventRef);
      const currentTotal = eventDoc.data()?.totalParticipants || 0;
      await updateDoc(eventRef, { totalParticipants: currentTotal + 1 });

      return { success: true };
    } catch (error) {
      console.error('Error adding participant to event:', error);
      throw error;
    }
  }
  ,
  // Actualizar un participante
  async updateParticipant(participantId, updateData) {
    try {
      const ref = doc(db, 'eventParticipants', participantId);
      await updateDoc(ref, {
        ...updateData,
        lastActivity: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating participant:', error);
      throw error;
    }
  },

  // Eliminar un participante
  async removeParticipant(participantId) {
    try {
      const pref = doc(db, 'eventParticipants', participantId);
      let eventId = null;
      try {
        const psnap = await getDoc(pref);
        eventId = psnap.exists() ? (psnap.data()?.eventId || null) : null;
      } catch {}
      await deleteDoc(pref);
      // Decrementar contador del evento si es posible
      if (eventId) {
        try {
          const eref = doc(db, 'events', eventId);
          const esnap = await getDoc(eref);
          const cur = esnap.exists() ? (esnap.data()?.totalParticipants || 0) : 0;
          await updateDoc(eref, { totalParticipants: Math.max(0, cur - 1) });
        } catch {}
      }
      return { success: true };
    } catch (error) {
      console.error('Error removing participant:', error);
      throw error;
    }
  }
};

// ===== SERVICIO DE ESTADÍSTICAS =====
export const statsService = {
  // Obtener estadísticas del organizador
  async getOrganizerStats(organizerId) {
    try {
      const events = await eventsService.getOrganizerEvents(organizerId);
      const eventIds = events.map(e => e.eventId);
      const now = Date.now();
      const day = 24*60*60*1000;
      const from30 = new Date(now - 30*day);
      const from60 = new Date(now - 60*day);
      const to30 = new Date(now - 30*day); // fin del período anterior
      // Totales reales por agregación, no por campos cacheados en el evento
      let totalPhotos = 0;
      let totalParticipants = 0;

      // Contadores por períodos (últimos 30 días y 30 días previos)
      let photosLast30 = 0, photosPrev30 = 0;
      let participantsLast30 = 0, participantsPrev30 = 0;

      // Consultar por evento para evitar where-in > 10
      for (const eid of eventIds) {
        try {
          // Total de fotos aprobadas del evento
          const qt = query(
            collection(db, 'photos'),
            where('eventId', '==', eid),
            where('status', '==', 'approved')
          );
          const ct = await getCountFromServer(qt);
          totalPhotos += ct.data().count;
        } catch {}
        try {
          // Total de participantes del evento
          const qp = query(
            collection(db, 'eventParticipants'),
            where('eventId', '==', eid)
          );
          const cp = await getCountFromServer(qp);
          totalParticipants += cp.data().count;
        } catch {}
        try {
          // Fotos aprobadas last30
          const q1 = query(
            collection(db, 'photos'),
            where('eventId', '==', eid),
            where('status', '==', 'approved'),
            where('uploadedAt', '>=', from30),
            orderBy('uploadedAt')
          );
          const c1 = await getCountFromServer(q1);
          photosLast30 += c1.data().count;
        } catch {}
        try {
          const q2 = query(
            collection(db, 'photos'),
            where('eventId', '==', eid),
            where('status', '==', 'approved'),
            where('uploadedAt', '>=', from60),
            where('uploadedAt', '<', to30),
            orderBy('uploadedAt')
          );
          const c2 = await getCountFromServer(q2);
          photosPrev30 += c2.data().count;
        } catch {}
        try {
          const q3 = query(
            collection(db, 'eventParticipants'),
            where('eventId', '==', eid),
            where('joinedAt', '>=', from30),
            orderBy('joinedAt')
          );
          const c3 = await getCountFromServer(q3);
          participantsLast30 += c3.data().count;
        } catch {}
        try {
          const q4 = query(
            collection(db, 'eventParticipants'),
            where('eventId', '==', eid),
            where('joinedAt', '>=', from60),
            where('joinedAt', '<', to30),
            orderBy('joinedAt')
          );
          const c4 = await getCountFromServer(q4);
          participantsPrev30 += c4.data().count;
        } catch {}
      }

      const photosGrowthPct = photosPrev30 === 0 ? (photosLast30 > 0 ? 100 : 0) : Math.round(((photosLast30 - photosPrev30) / photosPrev30) * 100);
      const participantsGrowthPct = participantsPrev30 === 0 ? (participantsLast30 > 0 ? 100 : 0) : Math.round(((participantsLast30 - participantsPrev30) / participantsPrev30) * 100);

      return {
        totalEvents: events.length,
        activeEvents: events.filter(e => e.status === 'active').length,
        inactiveEvents: events.filter(e => e.status !== 'active').length,
        totalPhotos,
        totalParticipants,
        photosLast30,
        photosPrev30,
        participantsLast30,
        participantsPrev30,
        photosGrowthPct,
        participantsGrowthPct
      };
    } catch (error) {
      console.error('Error getting organizer stats:', error);
      throw error;
    }
  }
};
